// The pipeline board by opportunity SALES stage (CEO 2026-10-15): Open →
// Awaiting Approval → Closed Won / Closed Lost (last 30 days). Stages move on
// their own (a quote is sent; a quote becomes an invoice) — the board shows
// where every opportunity is; the only manual moves are Mark lost (with a
// reason, on Open / Awaiting cards) and Reopen (on Lost cards). Each opportunity appears once; values are
// expected values, never quote totals.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { MarkLostDialog, staleAlertText, type OpportunitySales, type SalesStage } from "@/components/opportunity/OpportunitySales";

export interface OpportunityBoardResponse {
  available: boolean;
  columns: Array<{
    stage: SalesStage;
    label: string;
    count: number;
    value: number;
    cards: Array<{
      projectId: string;
      projectNumber: number;
      name: string;
      clientName: string;
      repName: string | null;
      sales: OpportunitySales;
    }>;
  }>;
}

const COLUMN_NOTE: Record<SalesStage, string> = {
  open: "Created — presentations and draft quotes",
  awaiting_approval: "A quote is with the client",
  closed_won: "Invoiced · last 30 days",
  closed_lost: "Last 30 days",
};

function money(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export function OpportunityBoardView(props: { board: OpportunityBoardResponse; scope: "all" | "mine"; setScope: (s: "all" | "mine") => void }) {
  const qc = useQueryClient();
  const [lostFor, setLostFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["sales"] });
    void qc.invalidateQueries({ queryKey: ["projects"] });
  };
  const snooze = useMutation({
    mutationFn: (projectId: string) => api(`/projects/${projectId}/snooze`, { method: "POST", body: "{}" }),
    onSuccess: refresh,
    onError: () => setError("Couldn't snooze that one — try again."),
  });
  const reopen = useMutation({
    mutationFn: (projectId: string) =>
      api(`/projects/${projectId}/sales-stage`, { method: "POST", body: JSON.stringify({ action: "reopen" }) }),
    onSuccess: refresh,
    onError: () => setError("Couldn't reopen that one — try again."),
  });
  const active = props.board.columns.filter((c) => c.stage === "open" || c.stage === "awaiting_approval");
  const pipeline = active.reduce((n, c) => n + c.value, 0);
  const stale = active.reduce((n, c) => n + c.cards.filter((k) => k.sales.stale).length, 0);

  return (
    <div className="mx-auto w-full">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Sales · Pipeline</p>
          <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">Opportunity pipeline</h1>
          <p className="mt-2 max-w-2xl text-[15px] text-muted">
            Every opportunity by stage. A sent quote moves it to Awaiting Approval; an invoice wins it.
          </p>
        </div>
        <div className="inline-flex overflow-hidden rounded-lg border border-rule">
          {(["all", "mine"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => props.setScope(s)}
              className={cn("px-3.5 py-1.5 text-sm font-semibold", props.scope === s ? "bg-ink text-white" : "bg-white text-ink hover:bg-cream-100")}
            >
              {s === "all" ? "Everyone" : "Mine"}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Active pipeline value" value={money(pipeline)} />
        <Stat label="Open · Awaiting approval" value={`${active[0]?.count ?? 0} · ${active[1]?.count ?? 0}`} />
        <Stat label="Need a look (21+ days)" value={String(stale)} warn={stale > 0} />
      </div>

      {error && <p role="alert" className="mb-4 rounded-md border border-rule bg-white px-4 py-3 text-sm text-risk">{error}</p>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {props.board.columns.map((col) => (
          <section key={col.stage} className="flex min-w-0 flex-col rounded-lg border border-rule bg-[#fafaf8]" aria-label={col.label}>
            <header className="border-b border-rule px-4 py-3">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-[15px] font-semibold text-ink">{col.label}</h2>
                <span className="text-sm tabular-nums text-muted">{col.count}</span>
              </div>
              <p className="mt-0.5 text-xs text-muted">{COLUMN_NOTE[col.stage]}</p>
              <p className="mt-1 text-sm font-medium tabular-nums text-ink">{money(col.value)}</p>
            </header>
            <ul className="flex flex-col gap-2 p-3">
              {col.cards.length === 0 && <li className="px-1 py-4 text-center text-sm text-muted">Nothing here.</li>}
              {col.cards.map((c) => (
                <li key={c.projectId} className={cn("rounded-md border bg-white p-3", c.sales.stale ? "border-warn" : "border-rule")}>
                  <Link to={`/projects/${c.projectId}`} className="block text-[15px] font-medium text-ink hover:underline">
                    {c.name}
                  </Link>
                  <p className="text-sm text-muted">
                    {c.clientName}
                    {c.repName ? ` · ${c.repName}` : ""}
                  </p>
                  <p className="mt-1 flex items-center justify-between gap-2 text-sm">
                    <span className="tabular-nums text-ink">{c.sales.expectedValue ? money(Number(c.sales.expectedValue)) : "No value yet"}</span>
                    <span className="text-muted">{c.sales.stageAgeDays}d</span>
                  </p>
                  {c.sales.stage === "closed_lost" && c.sales.lossReasonLabel && (
                    <p className="mt-1 text-xs text-muted">Lost: {c.sales.lossReasonLabel}</p>
                  )}
                  {!c.sales.stale && (c.sales.stage === "open" || c.sales.stage === "awaiting_approval") && (
                    <button type="button" className="mt-2 text-sm text-muted underline underline-offset-4 hover:text-ink" onClick={() => setLostFor(c.projectId)}>
                      Mark lost
                    </button>
                  )}
                  {c.sales.stage === "closed_lost" && (
                    <button
                      type="button"
                      className="mt-2 text-sm text-muted underline underline-offset-4 hover:text-ink"
                      disabled={reopen.isPending}
                      onClick={() => reopen.mutate(c.projectId)}
                    >
                      Reopen
                    </button>
                  )}
                  {c.sales.stale && (
                    <div className="mt-2 border-t border-rule pt-2">
                      <p className="flex items-start gap-1.5 text-sm text-warn">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                        {staleAlertText(c.sales.stageLabel, c.sales.stageAgeDays)}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/projects/${c.projectId}/quotes`}>Review opportunity</Link>
                        </Button>
                        <Button size="sm" variant="outline" disabled={snooze.isPending} onClick={() => snooze.mutate(c.projectId)}>
                          Snooze 7 days
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setLostFor(c.projectId)}>
                          Mark lost
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      {lostFor && (
        <MarkLostDialog
          projectId={lostFor}
          onClose={() => setLostFor(null)}
          onDone={() => {
            setLostFor(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-rule bg-white px-4 py-3">
      <p className="text-xs text-muted">{label}</p>
      <p className={cn("mt-1 text-[24px] font-medium tabular-nums", warn ? "text-warn" : "text-ink")}>{value}</p>
    </div>
  );
}
